import React, { useState, useEffect, useRef } from "react";
import { LEOPARD_ORANGE, APP_BG, LOGO, apiError, api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldAlert, Eye, EyeOff, Crown, Play, Pause, Music2, UserPlus, MessageCircle, Heart, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const GENDERS = ["Cisgender Female", "Cisgender Male", "Trans-Woman", "Trans-Man", "Non-Binary", "Queer", "Intersex"];
const SAMPLE_TRACK = "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3";
const PREVIEW_AVATAR = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

function ForgotDialog({ open, onOpenChange, fp, setFp }) {
  const request = async () => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fp.email });
      if (data.reset_token) setFp({ ...fp, token: data.reset_token, stage: "reset", error: "" });
      else setFp({ ...fp, error: "If that email exists, a reset link was created." });
    } catch (e) { setFp({ ...fp, error: apiError(e.response?.data?.detail) }); }
  };
  const reset = async () => {
    try {
      await api.post("/auth/reset-password", { token: fp.token, new_password: fp.pwd });
      toast.success("Password reset! You can log in now.");
      onOpenChange(false);
    } catch (e) { setFp({ ...fp, error: apiError(e.response?.data?.detail) }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-4 border-black bg-spotter-cream" data-testid="forgot-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-spotter-maroon">Reset your password</DialogTitle></DialogHeader>
        {fp.stage === "request" ? (
          <div className="space-y-3">
            <Label>Your email</Label>
            <Input data-testid="forgot-email" type="email" value={fp.email} onChange={(e) => setFp({ ...fp, email: e.target.value })} className="border-2 border-black" />
            {fp.error && <p className="text-sm text-spotter-maroon">{fp.error}</p>}
            <Button data-testid="forgot-submit" onClick={request} className="w-full bg-spotter-maroon text-spotter-cream border-2 border-black">Get reset link</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-spotter-maroon">Use this one-time reset below.</p>
            <Label>New password</Label>
            <Input data-testid="reset-password" type="password" value={fp.pwd} onChange={(e) => setFp({ ...fp, pwd: e.target.value })} className="border-2 border-black" />
            {fp.error && <p className="text-sm text-red-700">{fp.error}</p>}
            <Button data-testid="reset-submit" onClick={reset} className="w-full bg-spotter-maroon text-spotter-cream border-2 border-black">Set new password</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GateDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gate-dialog" className="border-4 border-black bg-spotter-cream max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2">
            <Crown className="w-6 h-6" style={{ color: "#E19C00" }} /> Like what you see?
          </DialogTitle>
          <DialogDescription className="text-black text-base pt-2">
            Well you should hurry and sign up so you can have a custom profile too! 🐆
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button data-testid="gate-signup" onClick={() => onOpenChange(false)}
            className="w-full bg-spotter-maroon text-spotter-cream border-2 border-black font-heading font-bold">
            Take me to sign up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewProfileDialog({ open, onOpenChange, onGate, data }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const name = data?.name || "A Spotter";
  const username = data?.username || "spotter";
  const spots = data?.spots ?? 0;
  const avatar = data?.avatar_url ? mediaUrl(data.avatar_url) : LOGO;
  const cover = data?.cover_url ? `url(${mediaUrl(data.cover_url)})` : `url(${LEOPARD_ORANGE})`;
  const bio = data?.bio || "Leopard at heart 🐆 · Earning my spots one post at a time. Sign up to see the whole profile!";

  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  }, [open]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => toast.error("Couldn't play the track.")); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="preview-dialog" className="border-4 border-black bg-spotter-cream p-0 overflow-hidden max-w-2xl">
        <div className="h-24 w-full" style={{ backgroundImage: cover, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="px-6 pb-6 -mt-12">
          <div className="flex items-end gap-4">
            <img src={avatar} alt={name} className="w-24 h-24 rounded-full border-4 border-black object-cover bg-white" />
            <div className="pb-1">
              <h3 className="font-display text-2xl font-bold text-spotter-maroon">{name}</h3>
              <p className="text-sm text-spotter-maroon/80">@{username} · {spots} spots</p>
            </div>
          </div>

          {/* Real music player — the ONLY interactive element for guests */}
          <div className="mt-5 border-2 border-black rounded-xl bg-white p-3 flex items-center gap-3 brand-shadow-sm" data-testid="preview-music-player">
            <button onClick={toggle} data-testid="preview-music-toggle"
              className="w-11 h-11 rounded-full bg-spotter-maroon text-spotter-cream border-2 border-black flex items-center justify-center hover:brightness-110 transition shrink-0">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 font-bold text-sm text-spotter-maroon"><Music2 className="w-4 h-4" /> Now Playing</div>
              <div className="text-xs text-spotter-maroon/70 truncate">Wild Nights — Spotter Sounds</div>
            </div>
            <audio ref={audioRef} src={SAMPLE_TRACK} onEnded={() => setPlaying(false)} preload="none" />
          </div>

          {/* Gatekept actions */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <button onClick={onGate} data-testid="preview-addfriend" className="flex flex-col items-center gap-1 border-2 border-black rounded-xl py-2.5 bg-spotter-gold/20 hover:bg-spotter-gold/40 transition text-spotter-maroon font-semibold text-xs">
              <UserPlus className="w-5 h-5" /> Add to Pride
            </button>
            <button onClick={onGate} data-testid="preview-message" className="flex flex-col items-center gap-1 border-2 border-black rounded-xl py-2.5 bg-spotter-gold/20 hover:bg-spotter-gold/40 transition text-spotter-maroon font-semibold text-xs">
              <MessageCircle className="w-5 h-5" /> Message
            </button>
            <button onClick={onGate} data-testid="preview-like" className="flex flex-col items-center gap-1 border-2 border-black rounded-xl py-2.5 bg-spotter-gold/20 hover:bg-spotter-gold/40 transition text-spotter-maroon font-semibold text-xs">
              <Heart className="w-5 h-5" /> Give a Paw
            </button>
          </div>

          <div className="mt-4 border-2 border-black rounded-xl bg-white p-4" onClick={onGate} data-testid="preview-aboutme">
            <h4 className="font-display font-bold text-spotter-maroon mb-1">About Me</h4>
            <p className="text-sm text-black/80">{bio}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Landing() {
  const { login, register } = useAuth();
  const { content } = useSiteContent();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", password2: "", legal_name: "", name: "", preferred_name: "", username: "", dob: "", gender: "", guardian_contact: "", legal_name_private: true, preferred_name_private: false, ref: new URLSearchParams(window.location.search).get("ref") || "" });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [fp, setFp] = useState({ email: "", token: "", pwd: "", stage: "request", error: "" });
  const [topSpotters, setTopSpotters] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [avail, setAvail] = useState({ email: null, username: null, name: null });
  const [checking, setChecking] = useState({ email: false, username: false, name: false });

  useEffect(() => {
    api.get("/public/top-spotters").then(({ data }) => setTopSpotters(data || [])).catch(() => {});
    api.get("/public/preview-profile").then(({ data }) => setPreview(data)).catch(() => {});
  }, []);

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
  const pw = form.password || "";
  const pwChecks = { length: pw.length >= 8, number: /\d/.test(pw), special: /[^A-Za-z0-9]/.test(pw) };
  const pwStrong = pwChecks.length && pwChecks.number && pwChecks.special;

  // Debounced live availability checks for email / username / display name (register only)
  useEffect(() => {
    if (mode !== "register") return;
    const fields = [["email", form.email], ["username", form.username], ["name", form.name]];
    const timers = fields.map(([field, raw]) => {
      const value = (raw || "").trim();
      if (!value || (field === "email" && !emailValid(value))) {
        setAvail((a) => ({ ...a, [field]: null }));
        setChecking((c) => ({ ...c, [field]: false }));
        return null;
      }
      setChecking((c) => ({ ...c, [field]: true }));
      return setTimeout(async () => {
        try {
          const { data } = await api.get("/auth/check-availability", { params: { field, value } });
          setAvail((a) => ({ ...a, [field]: data.available }));
        } catch { setAvail((a) => ({ ...a, [field]: null })); }
        finally { setChecking((c) => ({ ...c, [field]: false })); }
      }, 450);
    });
    return () => timers.forEach((t) => t && clearTimeout(t));
  }, [form.email, form.username, form.name, mode]);

  const emailFieldOk = emailValid(form.email) && avail.email === true;
  const usernameFieldOk = !!form.username.trim() && avail.username === true;
  const nameFieldOk = !!form.name.trim() && avail.name === true;
  const registerValid = emailFieldOk && usernameFieldOk && nameFieldOk && pwStrong && form.password === form.password2;

  const fieldState = (field) => {
    const raw = (form[field] || "").trim();
    if (!raw) return null;
    if (field === "email" && !emailValid(raw)) return false;
    return avail[field];
  };
  const borderCls = (field) => { const s = fieldState(field); return s === false ? "border-red-600" : s === true ? "border-green-600" : "border-black"; };
  const FieldMsg = ({ field, takenText }) => {
    const raw = (form[field] || "").trim();
    if (!raw) return null;
    if (field === "email" && !emailValid(raw)) return <p className="text-xs text-red-700 mt-1 flex items-center gap-1"><X className="w-3 h-3" />Enter a valid email address.</p>;
    if (checking[field]) return <p className="text-xs text-spotter-maroon mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Checking…</p>;
    if (avail[field] === false) return <p className="text-xs text-red-700 mt-1 flex items-center gap-1"><X className="w-3 h-3" />{takenText}</p>;
    if (avail[field] === true) return <p className="text-xs text-green-700 mt-1 flex items-center gap-1"><Check className="w-3 h-3" />Looks good!</p>;
    return null;
  };


  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ageFromDob = (dob) => {
    if (!dob) return null;
    const d = new Date(dob); if (isNaN(d)) return null;
    const t = new Date(); let a = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
    return a;
  };
  const isTeen = (() => { const a = ageFromDob(form.dob); return a !== null && a >= 16 && a <= 17; })();

  const switchTo = (m) => {
    setError("");
    if (m === "register") setShowAgeGate(true);
    setMode(m);
  };

  const openSignup = () => { setGateOpen(false); setPreviewOpen(false); switchTo("register"); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.gender) { setError("Please select your gender."); setLoading(false); return; }
        if (form.password !== form.password2) { setError("Passwords don't match."); setLoading(false); return; }
        if (isTeen && !form.guardian_contact.trim()) { setError("Members aged 16–17 must enter a parent/guardian's Spotter username or email."); setLoading(false); return; }
        localStorage.setItem("spotter_show_welcome", "1");
        const res = await register(form);
        if (res.pending_guardian) {
          localStorage.removeItem("spotter_show_welcome");
          setInfo(res.message || "Your account is waiting for guardian approval.");
          setMode("login");
        }
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-4"
      style={{ backgroundImage: `url(${APP_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="absolute inset-0" style={{ background: "rgba(82,2,2,0.35)" }} />

      <div className="relative w-full max-w-5xl">
        <div className="text-center mb-6">
          <img src={LOGO} alt="Spotter — Forget Your Stripes. Earn Your Spots." data-testid="landing-logo" className="mx-auto w-72 sm:w-96 drop-shadow-[3px_3px_0_rgba(0,0,0,0.6)]" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-spotter-cream -mt-8 sm:-mt-12 drop-shadow-[2px_2px_0_rgba(0,0,0,0.6)]" data-testid="landing-h1">Welcome to Spotter</h1>
          <p className="font-heading text-spotter-gold text-lg sm:text-xl font-bold mt-1" data-testid="landing-slogan">{content.slogan}</p>
          <p className="text-spotter-cream/90 text-sm mt-2 max-w-2xl mx-auto" data-testid="landing-subtitle">{content.landing_headline || "The social network where personality matters."}</p>
          {content.login_updates && (
            <div data-testid="landing-updates" className="mt-4 max-w-2xl mx-auto bg-spotter-gold/90 border-2 border-black rounded-xl px-4 py-2 text-sm font-semibold text-spotter-maroon">{content.login_updates}</div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-5 items-start">
          {/* LEFT: Top Spotters + preview */}
          <div className="bg-spotter-cream border-4 border-black rounded-2xl p-5 brand-shadow order-2 md:order-1" data-testid="landing-top-spotters">
            <h2 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2 mb-3"><Crown className="w-6 h-6" style={{ color: "#E19C00" }} />The Top Spotters</h2>
            {topSpotters.length === 0 ? (
              <p className="text-sm text-spotter-maroon">Be the first — sign up and claim your spot! 🐆</p>
            ) : (
              <div className="space-y-2">
                {topSpotters.map((u, i) => (
                  <button key={u.id} data-testid={`landing-spotter-${i}`} onClick={() => setGateOpen(true)}
                    className="w-full flex items-center gap-3 border-2 border-black rounded-xl p-2 bg-white hover:-translate-y-0.5 hover:bg-spotter-gold/20 transition text-left brand-shadow-sm">
                    <span className="font-display font-bold text-lg text-spotter-maroon w-6 shrink-0">{i + 1}</span>
                    <img src={u.avatar_url ? mediaUrl(u.avatar_url) : LOGO} alt={u.name} className="w-11 h-11 rounded-full border-2 border-black object-cover shrink-0 bg-white" />
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{u.name}</div>
                      <div className="text-xs text-spotter-maroon truncate">@{u.username} · {u.spots} spots</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button data-testid="landing-preview-btn" onClick={() => setPreviewOpen(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 font-heading font-bold text-spotter-cream bg-spotter-maroon border-2 border-black rounded-full py-2.5 brand-shadow-sm hover:brightness-110 transition">
              <Eye className="w-4 h-4" />Preview a profile
            </button>
          </div>

          {/* RIGHT: auth */}
          <div className="bg-spotter-cream border-4 border-black rounded-2xl p-7 brand-shadow order-1 md:order-2">
            <div className="flex mb-6 border-2 border-black rounded-full overflow-hidden">
              <button data-testid="tab-login" onClick={() => switchTo("login")}
                className={`flex-1 py-2 font-heading font-bold transition-colors ${mode === "login" ? "bg-spotter-maroon text-spotter-cream" : "bg-transparent text-black"}`}>Log In</button>
              <button data-testid="tab-register" onClick={() => switchTo("register")}
                className={`flex-1 py-2 font-heading font-bold transition-colors ${mode === "register" ? "bg-spotter-maroon text-spotter-cream" : "bg-transparent text-black"}`}>Sign Up</button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <>
                  <div>
                    <Label>Legal name <span className="text-red-700">*</span></Label>
                    <Input data-testid="register-legal-name" value={form.legal_name} onChange={set("legal_name")} required placeholder="Your legal first & last name" className="border-2 border-black" />
                    <label className="flex items-center gap-2 text-xs text-spotter-maroon mt-1">
                      <input type="checkbox" data-testid="register-legal-private" checked={form.legal_name_private} onChange={(e) => setForm({ ...form, legal_name_private: e.target.checked })} />
                      Keep my legal name private from the public (Owner & Admins can always see it)
                    </label>
                  </div>
                  <div>
                    <Label>Display name <span className="text-red-700">*</span></Label>
                    <Input data-testid="register-name" value={form.name} onChange={set("name")} required placeholder="Shown publicly across Spotter" className={`border-2 ${borderCls("name")}`} />
                    <FieldMsg field="name" takenText="That display name is taken." />
                  </div>
                  <div>
                    <Label>Preferred name (optional)</Label>
                    <Input data-testid="register-preferred" value={form.preferred_name} onChange={set("preferred_name")} placeholder="Also shown in your About Me" className="border-2 border-black" />
                    <label className="flex items-center gap-2 text-xs text-spotter-maroon mt-1">
                      <input type="checkbox" data-testid="register-preferred-private" checked={form.preferred_name_private} onChange={(e) => setForm({ ...form, preferred_name_private: e.target.checked })} />
                      Keep my preferred name private from the public
                    </label>
                  </div>
                  <div>
                    <Label>Username</Label>
                    <Input data-testid="register-username" value={form.username} onChange={set("username")} required placeholder="Hissy93" className={`border-2 ${borderCls("username")}`} />
                    <FieldMsg field="username" takenText="That username is taken." />
                  </div>
                  <div>
                    <Label>Date of birth</Label>
                    <Input data-testid="register-dob" type="date" value={form.dob} onChange={set("dob")} required className="border-2 border-black" />
                    <p className="text-xs text-spotter-maroon mt-1">You must be 16+. Members aged 16–17 need a parent/guardian to approve their account before they can log in.</p>
                  </div>
                  {isTeen && (
                    <div>
                      <Label>Parent / Guardian (their Spotter username or email)</Label>
                      <Input data-testid="register-guardian" value={form.guardian_contact} onChange={set("guardian_contact")} required placeholder="guardian's @username or email" className="border-2 border-black" />
                      <p className="text-xs text-spotter-maroon mt-1">They must already have a Spotter account and will need to approve you before you can log in.</p>
                    </div>
                  )}
                  <div>
                    <Label>Gender</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="register-gender" className="border-2 border-black bg-white"><SelectValue placeholder="Select your gender (required)" /></SelectTrigger>
                      <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input data-testid="login-email" type="email" value={form.email} onChange={set("email")} required placeholder="user@thetopspotter.com" className={`border-2 ${mode === "register" ? borderCls("email") : "border-black"}`} />
                {mode === "register" && <FieldMsg field="email" takenText="An account with this email already exists." />}
              </div>
              <div>
                <Label>Password</Label>
                <div className="relative">
                  <Input data-testid="login-password" type={showPwd ? "text" : "password"} value={form.password} onChange={set("password")} required placeholder="••••••••" className="border-2 border-black pr-10" />
                  <button type="button" data-testid="toggle-password" onClick={() => setShowPwd((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-spotter-maroon" aria-label="Show password">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {mode === "register" && (
                  <ul data-testid="password-checklist" className="mt-1.5 space-y-0.5">
                    {[["length", "At least 8 characters"], ["number", "At least one number"], ["special", "At least one special character"]].map(([k, label]) => (
                      <li key={k} data-testid={`pw-check-${k}`} className={`text-xs flex items-center gap-1 ${pwChecks[k] ? "text-green-700" : "text-spotter-maroon/70"}`}>
                        {pwChecks[k] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}{label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {mode === "register" && (
                <div>
                  <Label>Retype password</Label>
                  <div className="relative">
                    <Input data-testid="register-password2" type={showPwd2 ? "text" : "password"} value={form.password2} onChange={set("password2")} required placeholder="••••••••" className="border-2 border-black pr-10" />
                    <button type="button" data-testid="toggle-password2" onClick={() => setShowPwd2((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-spotter-maroon" aria-label="Show password">
                      {showPwd2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {form.password2 && form.password !== form.password2 && <p className="text-xs text-red-700 mt-1 flex items-center gap-1"><X className="w-3 h-3" />Passwords don't match.</p>}
                </div>
              )}
              {error && <p data-testid="auth-error" className="text-sm font-semibold text-red-700 bg-red-100 border border-red-300 rounded p-2">{error}</p>}
              {info && <p data-testid="auth-info" className="text-sm font-semibold text-spotter-maroon bg-spotter-gold/25 border-2 border-black rounded p-2">{info}</p>}
              <Button data-testid="auth-submit" type="submit" disabled={loading || (mode === "register" && !registerValid)}
                className="w-full bg-spotter-maroon hover:bg-spotter-maroon/90 text-spotter-cream font-heading font-bold text-base py-6 border-2 border-black brand-shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? "Please wait…" : mode === "login" ? "Enter Spotter" : "Join Spotter"}
              </Button>
              {mode === "login" && (
                <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotOpen(true); setFp({ email: form.email, token: "", pwd: "", stage: "request", error: "" }); }}
                  className="block w-full text-center text-sm text-spotter-maroon font-semibold hover:underline">Forgot your password?</button>
              )}
            </form>
          </div>
        </div>
      </div>

      <ForgotDialog open={forgotOpen} onOpenChange={setForgotOpen} fp={fp} setFp={setFp} />
      <PreviewProfileDialog open={previewOpen} onOpenChange={setPreviewOpen} onGate={() => setGateOpen(true)} data={preview} />
      <GateDialog open={gateOpen} onOpenChange={(v) => { if (!v) openSignup(); else setGateOpen(v); }} />

      <Dialog open={showAgeGate} onOpenChange={setShowAgeGate}>
        <DialogContent data-testid="age-gate-dialog" className="border-4 border-black bg-spotter-cream">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2 text-spotter-maroon">
              <ShieldAlert className="w-7 h-7" style={{ color: "#E19C00" }} /> Age & Safety Rules
            </DialogTitle>
            <DialogDescription className="text-black text-base pt-2 space-y-3">
              <span className="block"><b>No one under 16</b> is allowed to join Spotter.</span>
              <span className="block">Members aged <b>16 and 17</b> must appoint a parent/guardian (an existing Spotter member) from their profile. That guardian must <b>approve every incoming and outgoing Pride request</b> — keeping younger members safe.</span>
              <span className="block">By signing up you confirm your date of birth is accurate.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="age-gate-accept" onClick={() => setShowAgeGate(false)}
              className="bg-spotter-maroon text-spotter-cream border-2 border-black font-heading font-bold">I understand</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
